/**
 * 15 telas adicionais em um arquivo. Padrão CRUD/lista.
 * Componentes exportados casam com os slugs do MODULES_CATALOG.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
/*
 * ACOPLAMENTO ENTRE DOMINIOS, declarado.
 *
 * Esta e uma tela de OCPP e esta importando do modulo de FROTA. O que se
 * importa e `valorDeParcela`, que nao sabe nada de onibus: ela decide entre
 * os dois zeros ("zero por ausencia de topologia" e "zero por ausencia de
 * problema"), e a distincao vale para carregador igual vale para onibus.
 *
 * Fica REGISTRADO em vez de implicito: a funcao nasceu em `components/fleet`
 * porque foi la que o defeito apareceu, e o lugar certo dela e um modulo
 * comum (`lib/`) no dia em que houver o terceiro consumidor. Backlog
 * nomeado, nao ressalva circulando.
 */
import { valorDeParcela } from "../components/fleet/aggregates.js";
/*
 * O CONTRATO DE CARREGADOR VEM DE UM LUGAR SÓ — aqui havia DUAS das cinco
 * cópias (`WallCharger` e `LC`), divergindo entre si e das outras três.
 *
 * `WallCharger` tinha `activeTx` e conector com `errorCode`, e NÃO tinha
 * `location`, `firmware` nem `label`; `LC` tinha `activeTransactions[]` e nada
 * do resto. As duas tinham `lastHeartbeatAt` — campo REMOVIDO do contrato — e
 * nenhuma tinha `serialNumber`, `contact`, `source` nem `unsupportedFields`.
 */
import {
  configuracaoDeCarregadores,
  contatoApresentado,
  fabricanteEModelo,
  fonteForneceCapacidade,
  fraseDeFalhaDeComando,
  fraseDeRelacaoVazia,
  idadeCurtaDoContato,
  nomeDoCarregador,
  ROTULO_DO_MEIO_DE_CONTATO,
  semSinalDe,
  type RespostaDeAprovacoes,
  type RespostaDeCarregadores,
  type RespostaDeMonitorAoVivo,
  type RespostaDePainelWall,
  type SessaoAtivaDeCarregador,
} from './chargers-contrato.js';
import { fmtAge } from '../components/substation/bands.js';
import { maiuscula } from '../components/fleet/aggregates.js';
import { useAuth } from '../lib/auth-context.js';
import { useLiveEvent, type LiveEventPayload } from '../lib/useLiveEvents.js';
import { revalidarNavBadges } from '../lib/use-nav-badges.js';
import { LiveIndicator } from '../components/LiveIndicator.js';
import { IconBolt, IconChargers } from '../components/icons.js';
import { DownloadButton } from '../components/DownloadButton.js';
import { SessionDetailModal } from '../components/SessionDetailModal.js';

// ---------- helpers ----------
async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!r.ok) throw new Error(String(r.status));
  return (await r.json()) as T;
}

function useApi<T>(path: string, deps: unknown[] = [], intervalMs = 0) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    jsonFetch<T>(path)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    load();
    if (!intervalMs) return;
    const t = setInterval(load, intervalMs);
    return () => clearInterval(t);
  }, [load, intervalMs]);
  return { data, error, reload: load };
}

/**
 * Carimbo legível, com GUARDA DE `Invalid Date`.
 *
 * Sem a guarda, `new Date(x).toLocaleString()` devolve a string literal
 * `"Invalid Date"` para qualquer entrada que o parser recuse — e ela vai para a
 * tela como se fosse um horário. É a mesma família do `undefined` impresso: um
 * valor que não existe apresentado como valor.
 *
 * O caso não é hipotético nesta fronteira: o servidor entrega `contact.at` em
 * `'2026-10-31 19:20:13.667+00'` (sem `T`, sem `Z`), que V8 ACEITA por extensão
 * própria — mas é formato não-ISO, e o parser de data fora do padrão é
 * dependente de motor. A guarda custa uma linha e devolve o travessão, que é o
 * vocabulário certo para "não sei".
 */
function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function badge(text: string, kind: 'ok' | 'warn' | 'err' | 'neutral' = 'neutral') {
  const cls = {
    ok: 'nx-badge nx-badge--available',
    warn: 'nx-badge nx-badge--charging',
    err: 'nx-badge nx-badge--fault',
    neutral: 'nx-badge nx-badge--neutral',
  }[kind];
  return <span className={cls}>{text}</span>;
}

// ---------- CSV import modal (reused by Fleet + Drivers) ----------
interface ImportSummary {
  total: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: Array<{ line: number; message: string; raw: string }>;
}
interface CsvImportModalProps {
  title: string;
  columns: string[]; // header esperado, exibido na prévia
  postUrl: string;
  templateUrl: string;
  templateFilename: string;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Parser de CSV mínimo para PRÉVIA no browser. Não é production-grade: aceita
 * separador vírgula ou ponto-e-vírgula e aspas duplas simples. O parse real
 * (upsert no banco) acontece no servidor com csv-parse.
 */
function parsePreviewCsv(text: string): { headers: string[]; rows: string[][] } {
  const clean = text.replace(/^﻿/, '');
  const lines = clean.split(/\r?\n/).filter((l) => l.length > 0);
  if (!lines.length) return { headers: [], rows: [] };
  const sep = (lines[0]!.match(/;/g)?.length ?? 0) > (lines[0]!.match(/,/g)?.length ?? 0) ? ';' : ',';
  function splitLine(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (c === sep && !inQ) { out.push(cur); cur = ''; }
      else cur += c;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  }
  const headers = splitLine(lines[0]!);
  const rows = lines.slice(1).map(splitLine);
  return { headers, rows };
}

function CsvImportModal({ title, columns, postUrl, templateUrl, templateFilename, onClose, onDone }: CsvImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function readPreview() {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setPreviewError('Arquivo maior que 5MB. Reduza ou fatie o CSV.');
      setPreview(null);
      return;
    }
    try {
      const text = await file.text();
      const p = parsePreviewCsv(text);
      if (!p.headers.length) {
        setPreviewError('CSV vazio.');
        setPreview(null);
        return;
      }
      const missing = columns.filter((c) => !p.headers.includes(c));
      if (missing.length) {
        setPreviewError(`Coluna(s) obrigatória(s) ausente(s): ${missing.join(', ')}`);
        setPreview(null);
        return;
      }
      setPreviewError(null);
      setPreview(p);
    } catch (err) {
      setPreviewError((err as Error).message || 'Erro ao ler arquivo.');
    }
  }

  async function submit() {
    if (!file) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const r = await fetch(postUrl, { method: 'POST', body: form, credentials: 'include' });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        if (r.status === 413) setSubmitError('Arquivo maior que 5MB.');
        else if (r.status === 400) setSubmitError(`CSV inválido: ${body.error ?? 'formato'}`);
        else setSubmitError(`Falha (${r.status}): ${body.error ?? 'erro'}`);
        return;
      }
      const s = (await r.json()) as ImportSummary;
      setSummary(s);
    } catch (err) {
      setSubmitError((err as Error).message || 'Erro de rede.');
    } finally {
      setBusy(false);
    }
  }

  const previewRows = preview?.rows.slice(0, 10) ?? [];
  const totalPreview = preview?.rows.length ?? 0;

  return (
    <div className="nx-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="nx-modal__card" onClick={(e) => e.stopPropagation()}>
        <div className="nx-modal__head">
          <h2>{title}</h2>
          <button type="button" className="nx-modal__close" onClick={onClose} aria-label="Fechar">×</button>
        </div>
        {(() => {
          const step: 1 | 2 | 3 = summary ? 3 : preview ? 2 : 1;
          const steps: Array<[1 | 2 | 3, string]> = [[1, 'Arquivo'], [2, 'Prévia'], [3, 'Resumo']];
          return (
            <div className="nx-stepper" role="progressbar" aria-valuemin={1} aria-valuemax={3} aria-valuenow={step}
              style={{ display: 'flex', gap: 8, padding: '10px 18px 0', fontSize: 'var(--nx-text-xs)' }}>
              {steps.map(([n, label]) => (
                <span key={n}
                  style={{
                    flex: 1,
                    padding: '4px 10px',
                    borderRadius: 999,
                    background: n === step ? 'var(--nx-orange-500)' : n < step ? 'var(--nx-success-500)' : 'var(--nx-neutral-200)',
                    color: n <= step ? 'var(--nx-neutral-0)' : 'var(--nx-mute)',
                    fontWeight: 600,
                    textAlign: 'center',
                    letterSpacing: '0.02em',
                  }}>
                  {n}. {label}
                </span>
              ))}
            </div>
          );
        })()}
        <div className="nx-modal__body">
          {!summary && (
            <>
              <p style={{ color: 'var(--nx-mute-strong)', margin: 0, fontSize: 'var(--nx-text-sm)' }}>
                Envie um CSV com o cabeçalho: <code className="mono">{columns.join(',')}</code>.
                {' '}
                <a
                  href={templateUrl}
                  download={templateFilename}
                >
                  Baixar modelo
                </a>
              </p>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  setFile(e.currentTarget.files?.[0] ?? null);
                  setPreview(null);
                  setPreviewError(null);
                }}
              />
              {file && !preview && !previewError && (
                <button type="button" className="nx-btn nx-btn--ghost-dark" onClick={() => void readPreview()}>
                  Ler prévia
                </button>
              )}
              {previewError && (
                <p style={{ color: 'var(--nx-danger-500)', margin: 0 }}>{previewError}</p>
              )}
              {preview && (
                <>
                  <p style={{ margin: 0, fontSize: 'var(--nx-text-sm)', color: 'var(--nx-mute-strong)' }}>
                    Prévia de {previewRows.length} de {totalPreview} linhas:
                  </p>
                  <div className="nx-table-wrap" style={{ maxHeight: 260, overflow: 'auto' }}>
                    <table className="nx-table">
                      <thead>
                        <tr><th>#</th>{preview.headers.map((h) => <th key={h}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, i) => (
                          <tr key={i}><td className="mono">{i + 2}</td>{row.map((cell, j) => <td key={j} className="mono" style={{ fontSize: '.75rem' }}>{cell || <span style={{ color: 'var(--nx-mute)' }}>—</span>}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {submitError && <p style={{ color: 'var(--nx-danger-500)', margin: 0 }}>{submitError}</p>}
            </>
          )}
          {summary && (
            <>
              <div className="nx-kpi-grid">
                <div className="nx-kpi nx-kpi--success">
                  <div className="nx-kpi__label">Inseridos</div>
                  <div className="nx-kpi__value mono">{summary.inserted}</div>
                </div>
                <div className="nx-kpi nx-kpi--cyan">
                  <div className="nx-kpi__label">Atualizados</div>
                  <div className="nx-kpi__value mono">{summary.updated}</div>
                </div>
                <div className="nx-kpi nx-kpi--navy">
                  <div className="nx-kpi__label">Ignorados</div>
                  <div className="nx-kpi__value mono">{summary.skipped}</div>
                </div>
              </div>
              {summary.errors.length > 0 && (
                <>
                  <p style={{ margin: 0, fontSize: 'var(--nx-text-sm)', color: 'var(--nx-mute-strong)' }}>
                    {summary.errors.length} erro(s):
                  </p>
                  <div className="nx-table-wrap" style={{ maxHeight: 260, overflow: 'auto' }}>
                    <table className="nx-table">
                      <thead><tr><th>Linha</th><th>Motivo</th><th>Conteúdo</th></tr></thead>
                      <tbody>
                        {summary.errors.map((e, i) => (
                          <tr key={i} className="nx-modal__row--error">
                            <td className="mono">{e.line}</td>
                            <td className="mono">{e.message}</td>
                            <td className="mono" style={{ fontSize: '.7rem', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.raw}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="nx-modal__foot">
          {!summary && (
            <>
              <button type="button" className="nx-btn nx-btn--ghost-dark" onClick={onClose}>Cancelar</button>
              <button
                type="button"
                className="nx-btn"
                disabled={!preview || busy}
                onClick={() => void submit()}
              >
                {busy ? 'Enviando…' : `Importar ${totalPreview} registro(s)`}
              </button>
            </>
          )}
          {summary && (
            <button type="button" className="nx-btn" onClick={() => { onDone(); onClose(); }}>
              Fechar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- 1. Aprovação OCPP ----------
export function ChargerApprovalPage() {
  /*
    A FILA ENXUGOU, e o `id` SAIU do contrato.

    `RespostaDeAprovacoes.pending` publica `cpId, vendor, model, serialNumber,
    firmware, createdAt, source, sourceLabel, sourceVisible,
    decisionUnsupported` — e NÃO `id`. A declaração local aqui pedia `id` e o
    usava como `key` do React: com o campo ausente, todas as linhas passaram a
    ter `key={undefined}`, e o `tsc` não viu porque a declaração era local.
  */
  const { data, reload } = useApi<RespostaDeAprovacoes>('/api/charger-approvals', [], 5000);
  const [falha, setFalha] = useState<{ cpId: string; frase: string } | null>(null);
  async function act(cpId: string, action: 'approve' | 'deny') {
    setFalha(null);
    const r = await fetch(`/api/charger-approvals/${encodeURIComponent(cpId)}/${action}`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!r.ok) {
      const corpo = (await r.json().catch(() => null)) as unknown;
      setFalha({ cpId, frase: fraseDeFalhaDeComando(r.status, corpo) });
    }
    reload();
  }
  return (
    <div>
      <h1>Aprovação OCPP</h1>
      <p style={{ color: 'var(--nx-mute)' }}>Carregadores novos ficam aqui até você autorizar.</p>
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-4)' }}>
        {!data?.pending.length ? (
          <p style={{ color: 'var(--nx-mute)', textAlign: 'center', margin: 0 }}>Nenhum pedido pendente.</p>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead><tr>
                <th scope="col">ID do carregador</th>
                <th scope="col">Fabricante / Modelo</th>
                <th scope="col">Série / Firmware</th>
                <th scope="col">Fonte</th>
                <th scope="col">Registrado em</th>
                <th scope="col">Decisão</th>
              </tr></thead>
              <tbody>
                {data.pending.map((c) => (
                  /* `cpId` é a chave natural (`chargers_cp_id_unique`) e o
                     contrato não traz `id`. */
                  <tr key={c.cpId}>
                    <td className="mono">{c.cpId}</td>
                    {/*
                      O APELIDO DE FABRICANTE VALE AQUI TAMBÉM.

                      Esta célula imprimia `telluspower` cru enquanto
                      `/app/chargers` imprimia `Nansen` para o mesmo
                      equipamento — metade do portal contradizendo a outra
                      metade sobre o nome do fornecedor. `fabricanteEModelo`
                      também resolve a string vazia de `model`, que o `??` não
                      cobre e que fazia a célula sair "Nansen /" com o segundo
                      termo em branco.
                    */}
                    <td title={fabricanteEModelo(c).titulo}>
                      {fabricanteEModelo(c).texto}
                    </td>
                    <td className="mono" style={{ fontSize: '.75rem' }}>
                      {c.serialNumber ?? 'série não informada'}
                      <br />
                      {c.firmware ?? 'firmware não informado'}
                    </td>
                    <td>
                      {c.sourceLabel}
                      {/*
                        OS DOIS EIXOS SÃO ORTOGONAIS, e a tela afirma as duas
                        coisas E NEGA que uma implique a outra — junto do botão,
                        não em tooltip. Aprovar é "este equipamento pode operar
                        contra este portal"; visibilidade é "esta fonte aparece
                        nas telas". Aprovar NÃO torna a fonte visível.
                      */}
                      {!c.sourceVisible && (
                        <div style={{ fontSize: '.72rem', color: 'var(--nx-state-stale-ink)' }}>
                          Esta fonte está oculta nas telas. Aprovar aqui não a torna visível —
                          são decisões separadas.
                        </div>
                      )}
                    </td>
                    <td>{fmtDate(c.createdAt)}</td>
                    <td style={{ minWidth: 240 }}>
                      {/*
                        IMPOSSIBILIDADE DE DECISÃO: não existe decisão de
                        aprovação DESTE portal sobre equipamento cadastrado e
                        aprovado na origem. Os botões nascem desabilitados; o
                        409 que eles produziriam já está dito aqui.
                      */}
                      {c.decisionUnsupported ? (
                        <span
                          style={{ fontSize: '.78rem', color: 'var(--nx-mute)' }}
                          title={`A decisão de aprovação é do portal que fala OCPP com o equipamento. Os carregadores de ${c.sourceLabel} são cadastrados e aprovados na origem, e este portal não tem decisão de aprovação a tomar sobre eles.`}
                        >
                          Decisão da origem — cadastrado e aprovado em {c.sourceLabel}.
                        </span>
                      ) : (
                        <>
                          <button className="nx-btn nx-btn--sm" type="button" onClick={() => void act(c.cpId, 'approve')}>Aprovar</button>{' '}
                          <button className="nx-btn nx-btn--sm nx-btn--ghost-dark" type="button" onClick={() => void act(c.cpId, 'deny')}>Negar</button>
                        </>
                      )}
                      {falha?.cpId === c.cpId && (
                        <p role="status" aria-live="polite" style={{ fontSize: '.75rem', color: 'var(--nx-alert-ink)', margin: '6px 0 0' }}>
                          {falha.frase}
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- 2. Painel Wall ----------
function connectorTone(status: string): string {
  if (status === 'Charging') return 'nx-badge--charging';
  if (status === 'Available') return 'nx-badge--available';
  if (status === 'Preparing' || status === 'Finishing') return 'nx-badge--neutral';
  if (status === 'Faulted' || status === 'Unavailable') return 'nx-badge--fault';
  return 'nx-badge--neutral';
}
export function ChargingWallPage() {
  /*
    O ENVELOPE PUBLICADO, direto — sem camada de tradução.

    Havia um `WallCharger = LinhaCruaDeCarregador & {...}` aqui e um adaptador
    convertendo a linha crua. Os dois morreram: o tipo era `?: unknown` em todo
    campo (aceita qualquer objeto — afirmação, não verificação) e o backend
    passou a emitir `RespostaDePainelWall`, com `contact` ANINHADO. Alimentado
    com a forma nova, o adaptador lia `contactAt` (ausente) e devolvia
    `contact: { at: null, … }` — "sem carimbo" em 100% dos cards.
  */
  const { data } = useApi<RespostaDePainelWall>('/api/wall', [], 3000);
  const list = data?.chargers ?? [];
  /*
    O LIMIAR VEM DO SERVIDOR — e `configuration` É OBRIGATÓRIO neste envelope.

    A justificativa anterior ("`/api/wall` não publica `staleAfterSeconds`,
    então esta tela não classifica frescor") era verdadeira e DEIXOU DE SER. O
    custo dela estava medido: no mesmo instante, com os mesmos CPs, o Mural dizia
    `82791 → online, Carregando` enquanto `/app/chargers` dizia
    `82791 → Sem sinal, Sem contato há 87 min`. Duas telas, dado idêntico,
    afirmações opostas — e o CLAUDE.md lista esta tela como tela COM padrão STALE.
  */
  const cfg = configuracaoDeCarregadores(data?.configuration.staleAfterSeconds ?? 0);
  /*
    O SINAL DE VIVACIDADE NÃO É `connected` SOZINHO, E O PREDICADO É UM SÓ.

    Para `connectedMeans: 'source_status'` a frase que este mesmo código publica
    diz *"Não é uma conexão com este portal"* — usar esse booleano como prova de
    vivacidade é usar como sinal algo que o próprio texto nega ser isso.

    Este predicado estava escrito INLINE aqui e de novo no Monitor, com corpos
    idênticos entre si e divergentes do de `ChargersPage` — três cópias, e
    nenhuma cobrindo `sem-leitura`. Agora é `semSinalDe` de
    `chargers-contrato.ts`, importado pelas três telas.
  */
  const online = list.filter((c) => !semSinalDe(c, cfg)).length;
  /*
    O DENOMINADOR DE "CARREGANDO" NÃO É O PÁTIO, E O NUMERADOR TEM PORTA.

    `activeTx` sai de `transaction_visible`, vazia para a fonte que declara
    `transactions` fora de escopo: contar aqueles carregadores no denominador faz
    "0 de 19" significar "ninguém carrega" e "ninguém é medido" ao mesmo tempo.

    E o NUMERADOR herdava: `activeTx > 0` sem porta de sinal produzia
    **"2 carregadores · 1 online · 2 carregando"** — `carregando > online` é
    aritmeticamente impossível, e o card ao lado dizia "sem contato". Contador de
    transação do banco ≠ realidade quando o equipamento não responde.
  */
  const comSessoes = list.filter((c) => fonteForneceCapacidade(c, 'transactions'));
  const charging = comSessoes.filter((c) => c.activeTx > 0 && !semSinalDe(c, cfg)).length;
  const semSessoes = list.length - comSessoes.length;
  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__meta">
          <span className="nx-page-head__eyebrow">Operação</span>
          <h1>Mural de Pátio</h1>
          {/*
            OS DOIS ZEROS, na tela vizinha da que esta rodada consertou.

            Com a lista vazia o subtítulo dizia "0 carregadores · **0** online ·
            **0** carregando": o primeiro zero é verdade sobre a topologia, os
            outros dois são PARCELAS de um conjunto vazio e leem-se como "nenhum
            dos carregadores está online" — afirmação sobre carregadores que não
            existem. Mesma família do KPI "COM SINAL", e `valorDeParcela` já
            estava disponível.
          */}
          <p className="nx-page-head__sub">
            {list.length === 0 ? (
              'Nenhum carregador registrado'
            ) : (
              <>
                {list.length} carregador{list.length === 1 ? '' : 'es'} ·{' '}
                {valorDeParcela(online, list.length) ?? '—'} online ·{' '}
                {/* "Sem sinal: N" EM CONTAGEM PRÓPRIA — padrão STALE do
                    CLAUDE.md, e a tela irmã já dizia "17 sem sinal". Tirar os
                    stale do numerador sem declará-los esconde a existência
                    deles, que é o defeito oposto. */}
                {valorDeParcela(list.length - online, list.length) ?? '—'} sem sinal ·{' '}
                {valorDeParcela(charging, comSessoes.length) ?? '—'} carregando
                {comSessoes.length !== list.length
                  ? ` (de ${comSessoes.length} com sessões sincronizadas)`
                  : ''}
              </>
            )}
          </p>
          {/* A COBERTURA, DECLARADA: sem isto o "0 carregando" falava do pátio
              inteiro enquanto media só parte dele. */}
          {semSessoes > 0 && (
            <p className="nx-page-head__sub" style={{ color: 'var(--nx-mute)', margin: 0 }}>
              {semSessoes} {semSessoes === 1 ? 'carregador fica' : 'carregadores ficam'} fora da
              contagem de sessões — a fonte deles não sincroniza sessões nesta versão do portal.
            </p>
          )}
        </div>
        <div className="nx-page-head__actions">
          <span className="nx-live-badge nx-live-badge--on"><span className="nx-live-dot" /> ao vivo</span>
        </div>
      </div>

      {list.length === 0 && (
        <div className="nx-card" style={{ textAlign: 'center', color: 'var(--nx-mute)', padding: '2rem' }}>
          Nenhum carregador aprovado ainda. Aprove em <b>Aprovação OCPP</b>.
        </div>
      )}

      <div className="nx-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
        {list.map((c) => {
          const activeTx = c.activeTx;
          /*
            PADRÃO STALE, pelo MESMO critério da lista de Carregadores.

            Era `!c.connected` e nada mais. Medido no mesmo instante, mesmos CPs:
            esta tela dizia `82791 → online, Carregando` e `/app/chargers` dizia
            `82791 → Sem sinal, Sem contato há 87 min`, com
            `contact.ageSeconds 5418` contra `staleAfterSeconds 900`.
          */
          const contato = contatoApresentado(c, cfg, fmtAge);
          /* A DECISÃO vem pronta no objeto. */
          const stale = contato.semSinal;
          /*
            BUG-SANITY: "livre" ao lado de "#1 Charging" — e o conserto anterior
            cobria só UMA ocorrência.

            `conectorEmCarga` existia, mas era consultado APENAS no ramo
            `sessoesSincronizadas === false`. Para a fonte que SINCRONIZA sessões,
            `activeTx === 0` voltava a virar "livre" com o bocal reportando carga
            — medido ao vivo em `D24030483`:
            `● online | livre | #1 Charging | #2 Available`.

            A regra subiu para fora do ramo: o estado do conector é testemunho em
            QUALQUER fonte, e "livre" só é dito quando nenhum bocal contradiz.
          */
          const sessoesSincronizadas = fonteForneceCapacidade(c, 'transactions');
          const conectorEmCarga = c.connectors.some((k) =>
            ['charging', 'preparing', 'finishing', 'suspendedev', 'suspendedevse'].includes(
              k.status.trim().toLowerCase(),
            ),
          );
          const emCarga = activeTx > 0 || conectorEmCarga;
          const tone = stale ? 'navy' : emCarga ? 'orange' : sessoesSincronizadas ? 'success' : 'navy';
          const bigLabel = stale
            ? 'sem contato'
            : activeTx > 0
              ? `${activeTx} sess${activeTx > 1 ? 'ões' : 'ão'}`
              : conectorEmCarga
                /* O bocal diz carga e o contador de sessão não confirma: o que a
                   tela SABE é o estado do conector. Dizer "livre" aqui seria
                   escolher o número vazio contra o testemunho que existe. */
                ? 'conector em carga'
                : sessoesSincronizadas
                  ? 'livre'
                  : 'sem sessão sincronizada';
          const staleStyle: React.CSSProperties = stale
            /* Os NÚMEROS do padrão STALE do CLAUDE.md: `opacity .55` e
               `grayscale(.6)`. Estavam em .7/.5 — a intenção atendida com
               outros valores, que é como um padrão vira folclore. */
            ? { opacity: 0.55, filter: 'grayscale(0.6)' }
            : {};
          const meio = ROTULO_DO_MEIO_DE_CONTATO[c.contact.means];
          const nome = nomeDoCarregador(c);
          const marca = fabricanteEModelo(c);
          return (
            <div
              key={c.cpId}
              className={`nx-kpi nx-kpi--${tone}`}
              style={{ display: 'flex', flexDirection: 'column', gap: 10, ...staleStyle }}
              /* A frase longa vive UMA VEZ no cabeçalho; aqui ela é a dica do
                 card, porque num mural 16 parágrafos idênticos empurram a
                 informação para fora da tela. */
              title={
                stale
                  ? `Sem contato com este equipamento por ${c.sourceLabel} — os valores mostrados são o último estado recebido.`
                  : !sessoesSincronizadas
                    ? fraseDeRelacaoVazia('transaction_visible', [c.sourceLabel])
                    : undefined
              }
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className={nome === c.cpId ? 'mono' : undefined} style={{ fontSize: '.9rem', fontWeight: 700 }}>{nome}</div>
                {/*
                  O BADGE SEGUE O MESMO CRITÉRIO DO CARD.

                  Era `c.connected` sozinho: `● online` em verde num equipamento
                  cujo último contato tinha 87 min contra um limiar de 900 s. O
                  rótulo é NÃO-CROMÁTICO e nomeia a causa, porque "offline" seria
                  afirmar queda de link onde o que caiu foi o carimbo.
                */}
                {stale ? (
                  <span
                    className="nx-badge nx-badge--neutral"
                    title={contato.fraseDeConexao}
                  >
                    ● {contato.rotuloSemSinal}
                  </span>
                ) : (
                  <span className="nx-badge nx-badge--available" title={contato.fraseDeConexao}>
                    ● online
                  </span>
                )}
              </div>
              {/* A série/cp_id (chave natural) abaixo do nome, só quando o nome
                  exibido não é o próprio cpId — para não duplicar. */}
              {nome !== c.cpId && (
                <div className="mono" style={{ fontSize: '.72rem', color: 'var(--nx-mute)' }}>{c.cpId}</div>
              )}
              <div style={{ fontSize: stale || activeTx === 0 ? '1.1rem' : '1.5rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--nx-ink)' }}>
                {stale
                  ? <span style={{ color: 'var(--nx-mute)' }}>{bigLabel}</span>
                  : activeTx > 0
                    ? <span style={{ color: 'var(--nx-orange-500)' }}>⚡ {bigLabel}</span>
                    : conectorEmCarga
                      ? <span style={{ color: 'var(--nx-orange-ink)' }}>{bigLabel}</span>
                      : <span style={{ color: sessoesSincronizadas ? undefined : 'var(--nx-mute)' }}>{bigLabel}</span>}
              </div>
              {/* O APELIDO DE FABRICANTE VALE EM TODA TELA: `telluspower` saía
                  cru aqui enquanto `/app/chargers` imprimia `Nansen` para o
                  MESMO equipamento. `fabricanteEModelo` é o único lugar. */}
              <div style={{ fontSize: '.7rem', color: 'var(--nx-mute)' }} title={marca.titulo}>
                {marca.texto} · {c.sourceLabel}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {/* connectorId=0 é o status da station/EVSE (agregado OCPP),
                    não conector físico. Físicos começam em 1. */}
                {(() => {
                  const physical = c.connectors.filter((k) => k.connectorId > 0);
                  if (physical.length === 0) {
                    /* "sem conectores reportados" sugere espera. Para a fonte
                       que declara `connector_topology` impossível, esperar não
                       resolve: lá o conjunto de conectores vem do histórico de
                       sessões, e conector sem sessão não existe. */
                    return (
                      <span style={{ color: 'var(--nx-mute)', fontSize: '.75rem' }}>
                        {fonteForneceCapacidade(c, 'connector_topology')
                          ? 'sem conectores reportados'
                          : `topologia de conectores não publicada por ${c.sourceLabel}`}
                      </span>
                    );
                  }
                  return physical.map((k) => (
                    <span
                      key={k.connectorId}
                      className={`nx-badge ${stale ? 'nx-badge--neutral' : connectorTone(k.status)}`}
                      /* `errorCode` é o `_shown` — anulado pela fonte que não o
                         fornece. `title=""` é pior que ausente: existe e não
                         informa. */
                      title={
                        stale
                          ? `Último status conhecido: ${k.status}`
                          : (k.errorCode ?? undefined)
                      }
                    >
                      #{k.connectorId} {k.status}{stale ? ' (último)' : ''}
                    </span>
                  ));
                })()}
              </div>
              {/*
                Era `Último heartbeat: {fmtDate(c.lastHeartbeatAt)}` — campo
                REMOVIDO do contrato, que imprimia "—" para todos. O rótulo agora
                é decidido por `contact.means`: o equipamento falando ≠ o portal
                perguntando à fonte.
              */}
              <div
                style={{ fontSize: '.7rem', color: stale ? 'var(--nx-state-stale-ink)' : 'var(--nx-mute)' }}
                title={contato.fraseDeConexao}
              >
                {stale ? '⚠ ' : ''}
                {meio.curto}: {c.contact.at === null ? 'sem carimbo' : fmtDate(c.contact.at)}
                {/* A IDADE, e não só o relógio: um horário sozinho obriga o
                    operador a fazer a subtração de cabeça, e é ela que diz se o
                    dado sustenta a leitura. Vem de `fmtAge`, ASSINADA. */}
                {c.contact.at !== null && ` · ${idadeCurtaDoContato(c.contact, fmtAge)}`}
              </div>
              {/*
                A RESSALVA VEM PRONTA, e não rederivada aqui.

                Esta tela tinha a sua própria versão: rederivava
                `sharedBy !== null && sharedBy > 1` à mão e escrevia só
                "Carimbo compartilhado por 8 equipamentos de Portal VGPL" —
                **perdendo a segunda frase**, que é a única coisa que a ressalva
                tinha a dizer: *"É um carimbo da fonte, não 8 sinais
                independentes de equipamento."* Sem ela o operador lê um fato
                neutro de inventário em vez da ressalva que impede somar 8 no
                KPI de equipamento com problema.

                Eram TRÊS implementações da mesma frase; agora é uma.
              */}
              {contato.fraseDeCompartilhamento !== null && (
                <div style={{ fontSize: '.68rem', color: 'var(--nx-state-stale-ink)' }}>
                  {contato.fraseDeCompartilhamento}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- 3. Saúde da Bateria ----------
// Implementação atual: apps/web/src/pages/SaudeBateriaPage.tsx. A versão
// tabelada antiga (`BatteryHealthPage()` que consumia /api/battery-health)
// foi removida por estar órfã — não era roteada em AppShell nem importada
// em nenhum lugar, e ainda usava o antipadrão de exibir SoH sem estado
// vazio honesto (BUG-QA2-5).

// ---------- 4. Manutenção ----------
interface MOrder { id: number; cpId: string; kind: string; status: string; priority: string; title: string; description: string | null; scheduledFor: string | null; technician: string | null; completedAt: string | null; createdAt: string; }
export function MaintenancePage() {
  const [statusFilter, setStatus] = useState('open');
  const { data, reload } = useApi<{ orders: MOrder[] }>(`/api/maintenance?status=${statusFilter}`, [statusFilter], 10000);
  const [showForm, setShowForm] = useState(false);
  /* `/api/chargers` virou ENVELOPE. O tipo publicado traz `label` e
     `labelIsIdentifying`, que é o que permite o `<select>` abaixo mostrar um
     nome em vez de só o `cpId` — sem inventar nome quando ele não existe. */
  const { data: chargers } = useApi<RespostaDeCarregadores>('/api/chargers');
  /* As fontes que não sincronizam ordens de manutenção. É ESCOPO e não
     impossibilidade: o dado existe na origem, e ABRIR ordem por este portal
     FUNCIONA para estes carregadores — o que não vem é o histórico de lá. */
  const fontesSemHistorico = [
    ...new Set(
      (chargers?.chargers ?? [])
        .filter((c) => !fonteForneceCapacidade(c, 'maintenance_orders'))
        .map((c) => c.sourceLabel),
    ),
  ];

  async function create(e: FormEvent) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const body = {
      chargerId: Number(fd.get('chargerId')),
      kind: fd.get('kind') as string,
      priority: fd.get('priority') as string,
      title: fd.get('title') as string,
      description: fd.get('description') as string,
      technician: fd.get('technician') as string || undefined,
      scheduledFor: fd.get('scheduledFor') ? new Date(String(fd.get('scheduledFor'))).toISOString() : undefined,
    };
    await jsonFetch('/api/maintenance', { method: 'POST', body: JSON.stringify(body) });
    setShowForm(false);
    reload();
    // Ordem nova = pendência a mais. O contador do menu não tem push para
    // manutenção; sem este aviso o badge só acompanharia na varredura de 60s.
    revalidarNavBadges();
  }
  async function close(id: number) {
    if (!confirm('Fechar esta ordem?')) return;
    await fetch(`/api/maintenance/${id}/close`, { method: 'POST', credentials: 'include' });
    reload();
    // Uma pendência a menos: o menu precisa saber agora, senão fica exibindo
    // um número que esta tela já contradiz.
    revalidarNavBadges();
  }
  return (
    <div>
      <div className="nx-dash__head"><h1>Manutenção</h1><button className="nx-btn" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancelar' : '+ Nova ordem'}</button></div>
      <div style={{ marginBottom: 'var(--nx-space-4)' }}>
        {['open', 'in_progress', 'done', ''].map((s) => (
          <button key={s || 'all'} type="button" className={`nx-btn nx-btn--sm ${statusFilter === s ? '' : 'nx-btn--ghost-dark'}`} onClick={() => setStatus(s)} style={{ marginRight: 8 }}>
            {s === '' ? 'Todas' : s === 'open' ? 'Abertas' : s === 'in_progress' ? 'Em andamento' : 'Concluídas'}
          </button>
        ))}
      </div>
      {showForm && (
        <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
          <form onSubmit={create}>
            <div className="nx-filters">
              <label className="nx-field"><span>Carregador</span>
                {/* O rótulo acompanha o `cpId` SÓ quando identifica — rótulo
                    que repete fabricante/modelo não vira nome na lista. */}
                <select name="chargerId" required>
                  {(chargers?.chargers ?? []).map((c) => (
                    <option key={c.cpId} value={c.id}>
                      {nomeDoCarregador(c) === c.cpId ? c.cpId : `${nomeDoCarregador(c)} — ${c.cpId}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="nx-field"><span>Tipo</span>
                <select name="kind"><option value="corretiva">Corretiva</option><option value="preventiva">Preventiva</option><option value="inspecao">Inspeção</option></select>
              </label>
              <label className="nx-field"><span>Prioridade</span>
                <select name="priority" defaultValue="normal"><option value="low">Baixa</option><option value="normal">Normal</option><option value="high">Alta</option><option value="urgent">Urgente</option></select>
              </label>
              <label className="nx-field"><span>Título</span><input name="title" required /></label>
              <label className="nx-field"><span>Técnico responsável</span><input name="technician" placeholder="Nome ou equipe" /></label>
              <label className="nx-field"><span>Data prevista</span><input name="scheduledFor" type="datetime-local" /></label>
            </div>
            <label className="nx-field" style={{ marginTop: 'var(--nx-space-3)' }}><span>Descrição</span><textarea name="description" rows={3} style={{ width: '100%', padding: 'var(--nx-space-3)', border: '1px solid var(--nx-line)', borderRadius: 'var(--nx-radius-sm)', background: 'var(--nx-bg)', color: 'var(--nx-ink)' }} /></label>
            <button className="nx-btn" type="submit" style={{ marginTop: 'var(--nx-space-3)' }}>Criar</button>
          </form>
        </div>
      )}
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Charger</th><th>Tipo</th><th>Prior.</th><th>Título</th><th>Técnico</th><th>Status</th><th>Agendada</th><th></th></tr></thead>
          <tbody>
            {(data?.orders ?? []).map((o) => (
              <tr key={o.id}>
                <td className="mono">{o.cpId}</td>
                <td>{o.kind}</td>
                <td>{badge(o.priority, o.priority === 'urgent' || o.priority === 'high' ? 'err' : 'neutral')}</td>
                <td>{o.title}</td>
                <td>{o.technician ?? '—'}</td>
                <td>{badge(o.status, o.status === 'done' ? 'ok' : 'warn')}</td>
                <td>{fmtDate(o.scheduledFor)}</td>
                <td>{o.status !== 'done' && <button className="nx-btn nx-btn--sm" onClick={() => void close(o.id)}>Fechar</button>}</td>
              </tr>
            ))}
            {!data?.orders.length && (
              <tr>
                <td colSpan={8} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>
                  {/*
                    ESCOPO, não impossibilidade — e a diferença é acionável:
                    abrir ordem por este portal FUNCIONA para os carregadores
                    sincronizados (por isso `maintenance_orders` só está na lista
                    de quem não TRAZ o histórico). "Nenhuma ordem." seco sugere
                    que o pátio está sem manutenção, e não que o histórico da
                    origem não vem.
                  */}
                  {fontesSemHistorico.length === 0
                    ? 'Nenhuma ordem neste filtro.'
                    : `Nenhuma ordem neste filtro. ${fraseDeRelacaoVazia('maintenance_order_visible', fontesSemHistorico)}`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

// ---------- 5. Monitor ao Vivo ----------
export function LivePage() {
  /*
    O ENVELOPE PUBLICADO, direto.

    Aqui viviam a SEGUNDA cópia local da linha (`LC`, com `activeTransactions[]`
    e nada do resto) e uma `interface SessaoAtiva` que era duplicata literal dos
    sete campos de `SessaoAtivaDeCarregador`. As duas saíram: `/api/live` emite
    `RespostaDeMonitorAoVivo`, com `contact` aninhado e `configuration`
    obrigatório — medido contra o servidor reiniciado em 31/10/2026.

    O snapshot inclui o histórico para a lista não começar vazia; o push via
    `/ws/live` acrescenta os eventos novos entre as atualizações de 30 s.

    DOIS campos de impossibilidade SEPARADOS, e a separação é o ponto: a lista
    de carregadores ENCHE enquanto o rastro de eventos é impossível. Juntar os
    dois repetiria, no carregador, o defeito do contador derivado do Mapa.
  */
  const { data, reload } = useApi<RespostaDeMonitorAoVivo>('/api/live', [], 30_000);
  const [events, setEvents] = useState<Array<{ ts: string; text: string }>>([]);
  const historyLoaded = useRef(false);
  /* O limiar é do SERVIDOR, e `configuration` é obrigatório neste envelope. O
     predicado de sinal é o MESMO das outras duas telas — ver `semSinalDe` em
     `chargers-contrato.ts`, que nasceu de três cópias divergentes. */
  const cfg = configuracaoDeCarregadores(data?.configuration.staleAfterSeconds ?? 0);

  useEffect(() => {
    if (!data || historyLoaded.current) return;
    historyLoaded.current = true;
    const history = (data.recentEvents ?? []).map((event) => ({
      ts: event.createdAt,
      text: `OCPP ${event.direction === 'in' ? 'recebida' : 'enviada'} · ${event.cpId} · ${event.action}`,
    }));
    setEvents((current) =>
      [...history, ...current]
        .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
        .slice(0, 100),
    );
  }, [data]);

  const pushEvent = useCallback((ts: string, text: string) => {
    setEvents((prev) => [{ ts, text }, ...prev].slice(0, 100));
  }, []);

  useLiveEvent('transaction.started', (p: LiveEventPayload<'transaction.started'>, ts) => {
    pushEvent(ts, `transaction.started · ${p.cpId} #${p.connectorId} tx${p.txId} idTag=${p.idTag}`);
    void reload();
  });
  useLiveEvent('transaction.ended', (p: LiveEventPayload<'transaction.ended'>, ts) => {
    pushEvent(ts, `transaction.ended · ${p.cpId} tx${p.txId} ${(p.energyWh / 1000).toFixed(2)}kWh reason=${p.stopReason ?? '—'}`);
    void reload();
  });
  useLiveEvent('transaction.metervalue', (p: LiveEventPayload<'transaction.metervalue'>, ts) => {
    pushEvent(ts, `metervalue · ${p.cpId} tx${p.txId} ${(p.powerW / 1000).toFixed(2)}kW ${(p.energyWh / 1000).toFixed(3)}kWh${p.soc != null ? ` SoC=${p.soc}%` : ''}`);
  });
  useLiveEvent('charger.status', (p: LiveEventPayload<'charger.status'>, ts) => {
    pushEvent(ts, `charger.status · ${p.cpId} #${p.connectorId} → ${p.status}`);
    void reload();
  });

  return (
    <div>
      <div className="nx-dash__head"><h1>Monitor ao Vivo</h1><LiveIndicator /></div>
      <div className="nx-live-chargers">
        {(data?.chargers ?? []).map((c) => {
          const sessoes: SessaoAtivaDeCarregador[] = c.activeTransactions ?? [];
          /*
            PADRÃO STALE, pelo MESMO critério das outras duas telas.

            Era `!c.connected`. Sem contato — por link caído OU por carimbo que
            não sustenta a leitura — `activeTransactions` é o último estado antes
            do silêncio: NÃO mostrar "⚡ carregando" sobre número congelado é o
            terceiro item da lista de sanity do CLAUDE.md.
          */
          const contato = contatoApresentado(c, cfg, fmtAge);
          /* A DECISÃO vem pronta no objeto. */
          const stale = contato.semSinal;
          const visibleConnectors = c.connectors.filter((k) => k.connectorId > 0);
          const charging = sessoes.length > 0 || visibleConnectors.some((k) => k.status.toLowerCase() === 'charging');
          const faulted = visibleConnectors.some((k) => k.status.toLowerCase() === 'faulted');
          const status = stale ? 'offline' : charging ? 'charging' : faulted ? 'fault' : 'available';
          /* O rótulo de sem-sinal vem do `Record` exaustivo do contrato, igual
             nas três telas: "nunca reportou" ≠ "sem sinal" ≠ "sem conexão". */
          const statusLabel = stale
            ? maiuscula(contato.rotuloSemSinal ?? '')
            : charging
              ? 'Carregando'
              : faulted
                ? 'Com falha'
                : 'Disponível';
          const nome = nomeDoCarregador(c);
          const meio = ROTULO_DO_MEIO_DE_CONTATO[c.contact.means];
          const marca = fabricanteEModelo(c);
          return (
            <div
              key={c.cpId}
              className="nx-live-charger"
              data-status={status}
              title={stale ? contato.fraseDeConexao : undefined}
            >
              <div className="nx-live-charger__head">
                <span className="nx-live-charger__icon"><IconChargers /></span>
                <div className="nx-live-charger__identity">
                  <span className="nx-live-charger__eyebrow">Carregador</span>
                  <strong className={nome === c.cpId ? 'mono' : undefined}>{nome}</strong>
                  {/* Série/cp_id abaixo do nome, só quando o nome não é o cpId. */}
                  {nome !== c.cpId && (
                    <span className="nx-live-charger__eyebrow mono">{c.cpId}</span>
                  )}
                </div>
                <span className="nx-live-charger__status">
                  <span aria-hidden="true" />{statusLabel}
                </span>
              </div>
              <div className="nx-live-connectors" aria-label="Estado dos conectores">
                {visibleConnectors.length ? visibleConnectors.map((connector) => {
                  const state = stale ? 'offline' : connector.status.toLowerCase() === 'charging' ? 'charging' : connector.status.toLowerCase() === 'faulted' ? 'fault' : connector.status.toLowerCase() === 'available' ? 'available' : 'other';
                  const labels: Record<string, string> = {
                    Available: 'Disponível', Charging: 'Carregando', Faulted: 'Falha',
                    Preparing: 'Preparando', Finishing: 'Finalizando', Reserved: 'Reservado',
                    Unavailable: 'Indisponível', SuspendedEV: 'Pausado pelo veículo',
                    SuspendedEVSE: 'Pausado pelo carregador',
                  };
                  return (
                    <div className="nx-live-connector" data-status={state} key={connector.connectorId}>
                      <span className="nx-live-connector__number">Conector {connector.connectorId}</span>
                      <strong>{stale ? 'Sem sinal' : labels[connector.status] ?? connector.status}</strong>
                    </div>
                  );
                }) : (
                  <span className="nx-live-connectors__empty">
                    {fonteForneceCapacidade(c, 'connector_topology')
                      ? 'Nenhum conector reportado'
                      : `Topologia de conectores não publicada por ${c.sourceLabel}`}
                  </span>
                )}
              </div>
              {/*
                A ÚLTIMA EVIDÊNCIA DE TEMPO, com `means` grudado nela.

                Este card não tinha nenhuma: dizia "Sem sinal" sem nunca mostrar
                desde quando nem por qual caminho. Com duas fontes visíveis,
                "este equipamento parou" e "a integração parou" são causas
                diferentes.
              */}
              <div
                className="nx-live-charger__eyebrow"
                style={{
                  fontSize: '.7rem',
                  color: stale ? 'var(--nx-state-stale-ink)' : 'var(--nx-mute)',
                }}
                title={contato.fraseDeConexao}
              >
                {marca.texto} · {c.sourceLabel}
                <br />
                {meio.curto}: {c.contact.at === null ? 'sem carimbo' : fmtDate(c.contact.at)}
                {c.contact.at !== null && ` · ${idadeCurtaDoContato(c.contact, fmtAge)}`}
              </div>
              {/* A MESMA ressalva pronta — ver o comentário no Mural. Aqui ela
                  também vinha rederivada à mão e sem a segunda frase. */}
              {contato.fraseDeCompartilhamento !== null && (
                <div
                  className="nx-live-charger__eyebrow"
                  style={{ fontSize: '.68rem', color: 'var(--nx-state-stale-ink)' }}
                >
                  {contato.fraseDeCompartilhamento}
                </div>
              )}
              {sessoes.length > 0 && (
                <div className="nx-live-sessions">
                  {sessoes.map((t) => {
                    const minutes = Math.max(0, Math.floor((Date.now() - new Date(t.startTime).getTime()) / 60_000));
                    return (
                      <section className="nx-live-session" key={t.ocppTransactionId}>
                        <div className="nx-live-session__head">
                          <span className="nx-live-session__label"><IconBolt /> Sessão #{t.ocppTransactionId}</span>
                          <span className="nx-live-session__vehicle">{t.chassiVin ? `Veículo ${t.chassiVin}` : `RFID ${t.idTag}`}</span>
                        </div>
                        <div className="nx-live-session__metrics">
                          <div><span>Energia</span><strong>{t.energyWh == null ? '—' : `${(t.energyWh / 1000).toFixed(2)} kWh`}</strong></div>
                          <div><span>Potência</span><strong>{t.powerW == null ? '—' : `${(t.powerW / 1000).toFixed(1)} kW`}</strong></div>
                          <div><span>Duração</span><strong>{minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}min`}</strong></div>
                        </div>
                        {stale && <small className="nx-live-session__stale">Última leitura conhecida</small>}
                      </section>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {data && data.chargers.length === 0 && (
          <div className="nx-live-empty">
            <span className="nx-live-empty__icon"><IconChargers /></span>
            <strong>Nenhum carregador nesta unidade</strong>
            <span>Os equipamentos aparecerão aqui quando forem cadastrados.</span>
          </div>
        )}
        {!data && (
          <div className="nx-live-empty" aria-live="polite">
            <span className="nx-live-empty__icon nx-live-empty__icon--loading"><IconChargers /></span>
            <strong>Conectando aos carregadores</strong>
            <span>Buscando o estado mais recente da operação.</span>
          </div>
        )}
      </div>
      <section className="nx-card nx-live-events">
        <div className="nx-live-events__head">
          <div>
            <h2>Atividade recente</h2>
            <p>Mensagens dos carregadores e eventos da operação</p>
          </div>
          <span className="nx-live-events__count">{events.length} / 100</span>
        </div>
        {!events.length ? (
          <div className="nx-live-events__empty" role="status">
            <span className="nx-live-events__empty-icon"><IconChargers /></span>
            <div>
              {/*
                IMPOSSIBILIDADE × ESPERA, e os dois vocabulários não se
                misturam.

                `eventsUnsupported` é `true` quando NENHUMA fonte visível
                alimenta `ocpp_messages`. Aí "Nenhum evento registrado AINDA" e
                "Novas mensagens aparecerão aqui automaticamente" são promessas
                falsas: não há caminho. A frase vem pronta do servidor, que é
                quem conhece a topologia de fontes.

                A lista de carregadores ao lado continua CHEIA — por isso os dois
                campos de impossibilidade são separados no contrato.
              */}
              {data?.eventsUnsupported === true ? (
                <>
                  <strong>Esta fonte não fala OCPP com este portal</strong>
                  <span>
                    {data.eventsUnsupportedBy?.message ??
                      fraseDeRelacaoVazia('ocpp_message_visible', [])}
                  </span>
                </>
              ) : (
                <>
                  <strong>{data ? 'Nenhum evento registrado ainda' : 'Carregando atividade'}</strong>
                  <span>{data ? 'Novas mensagens aparecerão aqui automaticamente.' : 'Buscando os últimos registros da operação.'}</span>
                </>
              )}
            </div>
          </div>
        ) : (
          <ul className="nx-live-event-list">
            {events.map((e, i) => (
              <li key={`${e.ts}-${i}`}>
                <time dateTime={e.ts}>{new Date(e.ts).toLocaleTimeString('pt-BR')}</time>
                <span>{e.text}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------- 6. Fases de Carga ----------
interface Phase { id: number; ocppTransactionId: number; cpId: string; startTime: string; stopTime: string | null; energyWh: number | null; samples: number; avgPowerW: number; finalSoc: number; }
export function ChargePhasesPage() {
  const { data } = useApi<{ transactions: Phase[] }>('/api/charge-phases', [], 15000);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  return (
    <div>
      <h1>Etapas da Recarga</h1>
      <p style={{ color: 'var(--nx-mute)' }}>Última centena de transações com estatísticas agregadas das leituras.</p>
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>#</th><th>Charger</th><th>Início</th><th>Fim</th><th>Amostras</th><th>Pot. média</th><th>Energia</th><th>SoC final</th></tr></thead>
          <tbody>
            {(data?.transactions ?? []).map((t) => (
              <tr key={t.ocppTransactionId} onClick={() => setSelectedSessionId(t.id)} style={{ cursor: 'pointer' }} title="Abrir análise completa da sessão">
                <td className="mono">#{t.ocppTransactionId}</td>
                <td className="mono">{t.cpId}</td>
                <td>{fmtDate(t.startTime)}</td>
                <td>{t.stopTime ? fmtDate(t.stopTime) : <em>em curso</em>}</td>
                <td className="mono">{t.samples}</td>
                <td className="mono">{(t.avgPowerW / 1000).toFixed(2)} kW</td>
                <td className="mono">{t.energyWh == null ? '—' : (t.energyWh / 1000).toFixed(3) + ' kWh'}</td>
                <td>{t.finalSoc ? badge(`${t.finalSoc}%`, t.finalSoc >= 80 ? 'ok' : 'neutral') : '—'}</td>
              </tr>
            ))}
            {!data?.transactions.length && <tr><td colSpan={8} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem transações.</td></tr>}
          </tbody>
        </table>
      </div></div>
      {selectedSessionId !== null && <SessionDetailModal sessionId={selectedSessionId} onClose={() => setSelectedSessionId(null)} />}
    </div>
  );
}

// ---------- 7. Logs OCPP ----------
export function OcppLogsPage() {
  const [cpFilter, setCpFilter] = useState('');
  /*
    `unsupported` + `unsupportedBy` entraram no contrato desta rota, e sem eles
    `messages: []` é indistinguível de "nenhuma mensagem AINDA" — a frase que
    promete que esperar resolve.

    `unsupported` é `true` só quando NENHUMA fonte visível alimenta
    `ocpp_messages`. Com fontes mistas ele vem `false` e a lista pode estar vazia
    por outro motivo; aí quem nomeia a impossibilidade PARCIAL é
    `sources[].unsupportedFields` — e é por isso que a nota de rodapé existe
    separada da frase de tabela vazia.
  */
  const { data } = useApi<{
    messages: Array<{ id: number; cpId: string; direction: string; action: string; uid: string; payload: string; createdAt: string }>;
    sources?: Array<{ source: string; label: string; unsupportedFields: readonly string[] }>;
    unsupported?: boolean;
    unsupportedBy?: { source: string; sourceLabel: string; message: string };
  }>(`/api/ocpp-logs${cpFilter ? '?cpId=' + encodeURIComponent(cpFilter) : ''}`, [cpFilter], 10000);
  const impossivel = data?.unsupported === true;
  /* As fontes VISÍVEIS que não alimentam esta tela. Com `nxvolt_ocpp` e `vgpl`
     visíveis ao mesmo tempo, `unsupported` é falso e esta lista é a única coisa
     que explica por que metade do pátio nunca aparece aqui. */
  const fontesMudas = (data?.sources ?? [])
    .filter((f) => f.unsupportedFields.includes('ocpp_messages'))
    .map((f) => f.label);
  return (
    <div>
      <h1>Comunicação dos Carregadores</h1>
      {impossivel && (
        <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }} role="status">
          <strong>Esta fonte não fala OCPP com este portal</strong>
          <p style={{ color: 'var(--nx-mute)', marginBottom: 0 }}>
            {data?.unsupportedBy?.message ?? fraseDeRelacaoVazia('ocpp_message_visible', [])}
          </p>
        </div>
      )}
      {!impossivel && fontesMudas.length > 0 && (
        <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
          <p style={{ color: 'var(--nx-mute)', margin: 0 }}>
            {fraseDeRelacaoVazia('ocpp_message_visible', fontesMudas)} Os equipamentos das
            demais fontes continuam aparecendo abaixo.
          </p>
        </div>
      )}
      <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
        {/*
          FILTRO DESABILITADO quando não há o que filtrar: oferecer um campo
          sobre nada sugere que algum valor traria resultado.
        */}
        <label className="nx-field" style={{ maxWidth: 300 }}>
          <span>Filtrar por ID do carregador</span>
          <input
            value={cpFilter}
            onChange={(e) => setCpFilter(e.target.value)}
            placeholder={impossivel ? 'Sem mensagens para filtrar' : 'ex: NX-SIM-01'}
            disabled={impossivel}
            aria-describedby={impossivel ? 'ocpp-filtro-desabilitado' : undefined}
          />
        </label>
        {impossivel && (
          <p id="ocpp-filtro-desabilitado" style={{ color: 'var(--nx-mute)', fontSize: '.8rem', marginBottom: 0 }}>
            Nenhuma fonte visível entrega mensagens OCPP a este portal, então não há o que filtrar.
          </p>
        )}
      </div>
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr>
            <th scope="col">Quando</th>
            <th scope="col">Carregador</th>
            <th scope="col">Direção</th>
            <th scope="col">Ação</th>
            <th scope="col">UID</th>
            <th scope="col">Payload</th>
          </tr></thead>
          <tbody>
            {(data?.messages ?? []).map((m) => (
              <tr key={m.id}>
                <td className="mono" style={{ fontSize: '.75rem' }}>{fmtDate(m.createdAt)}</td>
                <td className="mono">{m.cpId}</td>
                <td>{badge(m.direction === 'in' ? '← in' : 'out →', m.direction === 'in' ? 'neutral' : 'ok')}</td>
                <td className="mono">{m.action}</td>
                <td className="mono" style={{ fontSize: '.7rem', color: 'var(--nx-mute)' }}>{m.uid.slice(0, 8)}</td>
                <td className="mono" style={{ fontSize: '.7rem', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.payload}</td>
              </tr>
            ))}
            {!data?.messages.length && (
              <tr>
                <td colSpan={6} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>
                  {/* IMPOSSIBILIDADE não é "sem mensagens": a primeira nega o
                      caminho, a segunda sugere que ele existe e está ocioso. */}
                  {impossivel
                    ? (data?.unsupportedBy?.message ??
                       fraseDeRelacaoVazia('ocpp_message_visible', []))
                    : 'Nenhuma mensagem registrada no período.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

// ---------- 8. Telemetria Sub. ----------
// A tela de Subestacao saiu deste arquivo: virou `pages/TelemetriaSubPage.tsx`,
// lazy, com CSS e grafico proprios. A numeracao das secoes fica como estava
// para nao renumerar as sete seguintes por causa de uma mudanca de arquivo.

// ---------- 9. Acessos API ----------
export function ApiAccessPage() {
  const { data, reload } = useApi<{ tokens: Array<{ id: number; name: string; prefix: string; scopes: string; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }> }>('/api/api-tokens');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  async function create(e: FormEvent) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const r = await jsonFetch<{ token: string }>('/api/api-tokens', { method: 'POST', body: JSON.stringify({ name: fd.get('name'), scopes: fd.get('scopes') }) });
    setNewToken(r.token);
    setShowForm(false);
    reload();
  }
  async function revoke(id: number) {
    if (!confirm('Revogar este token?')) return;
    await fetch(`/api/api-tokens/${id}/revoke`, { method: 'POST', credentials: 'include' });
    reload();
  }
  return (
    <div>
      <div className="nx-dash__head"><h1>Chaves de Integração</h1><button className="nx-btn" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancelar' : '+ Novo token'}</button></div>
      {newToken && (
        <div className="nx-card nx-tempcred">
          <strong>Token gerado (aparece 1x):</strong>
          <code className="mono nx-tempcred__code">{newToken}</code>
          <button className="nx-btn nx-btn--sm nx-btn--ghost-dark" onClick={() => setNewToken(null)} style={{ marginTop: 'var(--nx-space-3)' }}>Copiei, fechar</button>
        </div>
      )}
      {showForm && (
        <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
          <form onSubmit={create}>
            <div className="nx-filters">
              <label className="nx-field"><span>Nome</span><input name="name" required /></label>
              <label className="nx-field"><span>Scopes (csv)</span><input name="scopes" defaultValue="read" /></label>
            </div>
            <button type="submit" className="nx-btn" style={{ marginTop: 'var(--nx-space-3)' }}>Criar</button>
          </form>
        </div>
      )}
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Nome</th><th>Prefixo</th><th>Scopes</th><th>Criado</th><th>Último uso</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {(data?.tokens ?? []).map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td className="mono">{t.prefix}…</td>
                <td className="mono">{t.scopes}</td>
                <td>{fmtDate(t.createdAt)}</td>
                <td>{fmtDate(t.lastUsedAt)}</td>
                <td>{t.revokedAt ? badge('revogado', 'err') : badge('ativo', 'ok')}</td>
                <td>{!t.revokedAt && <button className="nx-btn nx-btn--sm nx-btn--ghost-dark" onClick={() => void revoke(t.id)}>Revogar</button>}</td>
              </tr>
            ))}
            {!data?.tokens.length && <tr><td colSpan={7} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Nenhum token.</td></tr>}
          </tbody>
        </table>
      </div></div>
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-6)' }}>
        <h2 style={{ fontSize: '1rem' }}>API REST para integrações</h2>
        <p style={{ color: 'var(--nx-mute)' }}>Use o token como <code className="mono">Authorization: Bearer nxv_…</code>. O token só é mostrado na criação e pode ser revogado a qualquer momento.</p>
        <div className="nx-table-wrap"><table className="nx-table"><thead><tr><th>Método</th><th>Endpoint</th><th>Dados</th></tr></thead><tbody>
          <tr><td className="mono">GET</td><td className="mono">/api/integrations/v1/chargers</td><td>Carregadores, conectores e estado atual.</td></tr>
          <tr><td className="mono">GET</td><td className="mono">/api/integrations/v1/sessions?from=&amp;to=&amp;limit=</td><td>Sessões concluídas, energia, VIN quando atribuído e motivo de parada.</td></tr>
        </tbody></table></div>
      </div>
    </div>
  );
}

// ---------- 10. Veículos (fleet) ----------
interface Vehicle { id: number; plate: string; prefix: string | null; make: string | null; model: string | null; year: number | null; capacityKwh: number | null; active: boolean; driver: { id: number; name: string } | null; }
export function FleetPage() {
  const { data, reload } = useApi<{ vehicles: Vehicle[] }>('/api/vehicles');
  const { data: drivers } = useApi<{ drivers: Array<{ id: number; name: string }> }>('/api/drivers');
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  async function create(e: FormEvent) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const body: Record<string, unknown> = {
      plate: fd.get('plate'),
      prefix: fd.get('prefix') || undefined,
      make: fd.get('make') || undefined,
      model: fd.get('model') || undefined,
      year: fd.get('year') ? Number(fd.get('year')) : undefined,
      capacityKwh: fd.get('capacityKwh') ? Number(fd.get('capacityKwh')) : undefined,
      driverId: fd.get('driverId') ? Number(fd.get('driverId')) : undefined,
    };
    await jsonFetch('/api/vehicles', { method: 'POST', body: JSON.stringify(body) });
    setShowForm(false);
    reload();
  }
  return (
    <div>
      <div className="nx-dash__head">
        <h1>Veículos</h1>
        <div style={{ display: 'flex', gap: 'var(--nx-space-3)' }}>
          <button className="nx-btn nx-btn--ghost-dark" onClick={() => setShowImport(true)}>Importar CSV</button>
          <button className="nx-btn" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancelar' : '+ Novo veículo'}</button>
        </div>
      </div>
      {showImport && (
        <CsvImportModal
          title="Importar veículos"
          columns={['plate', 'make', 'model', 'year', 'capacityKwh', 'driverId']}
          postUrl="/api/vehicles/import-csv"
          templateUrl="/api/vehicles/import-csv/template"
          templateFilename="vehicles-template.csv"
          onClose={() => setShowImport(false)}
          onDone={reload}
        />
      )}
      {showForm && (
        <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
          <form onSubmit={create}>
            <div className="nx-filters">
              <label className="nx-field"><span>Prefixo</span><input name="prefix" placeholder="ex: 00015" /></label>
              <label className="nx-field"><span>Placa</span><input name="plate" required /></label>
              <label className="nx-field"><span>Fabricante</span><input name="make" /></label>
              <label className="nx-field"><span>Modelo</span><input name="model" /></label>
              <label className="nx-field"><span>Ano</span><input name="year" type="number" /></label>
              <label className="nx-field"><span>Capacidade (kWh)</span><input name="capacityKwh" type="number" /></label>
              <label className="nx-field"><span>Motorista</span>
                <select name="driverId" defaultValue=""><option value="">—</option>{(drivers?.drivers ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
              </label>
            </div>
            <button type="submit" className="nx-btn" style={{ marginTop: 'var(--nx-space-3)' }}>Criar</button>
          </form>
        </div>
      )}
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Prefixo</th><th>Placa</th><th>Fabricante / Modelo</th><th>Ano</th><th>Capacidade</th><th>Motorista</th><th>Status</th></tr></thead>
          <tbody>
            {(data?.vehicles ?? []).map((v) => (
              <tr key={v.id}>
                <td className="mono" style={{ fontWeight: 700 }}>{v.prefix ?? <span style={{ color: 'var(--nx-mute)', fontWeight: 400 }}>—</span>}</td>
                <td className="mono">{v.plate}</td>
                <td>{v.make ?? '—'} / {v.model ?? '—'}</td>
                <td>{v.year ?? '—'}</td>
                <td className="mono">{v.capacityKwh ? `${v.capacityKwh} kWh` : '—'}</td>
                <td>{v.driver?.name ?? <span style={{ color: 'var(--nx-mute)' }}>—</span>}</td>
                <td>{badge(v.active ? 'ativo' : 'inativo', v.active ? 'ok' : 'err')}</td>
              </tr>
            ))}
            {!data?.vehicles.length && <tr><td colSpan={7} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem veículos.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

// ---------- 11. Motoristas ----------
interface Driver { id: number; name: string; document: string | null; email: string | null; phone: string | null; idTag: string | null; active: boolean; }
export function DriversPage() {
  const { data, reload } = useApi<{ drivers: Driver[] }>('/api/drivers');
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  async function create(e: FormEvent) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const body: Record<string, unknown> = { name: fd.get('name') };
    for (const k of ['document', 'email', 'phone', 'idTag'] as const) {
      const v = fd.get(k);
      if (v) body[k] = v;
    }
    await jsonFetch('/api/drivers', { method: 'POST', body: JSON.stringify(body) });
    setShowForm(false);
    reload();
  }
  return (
    <div>
      <div className="nx-dash__head">
        <h1>Motoristas</h1>
        <div style={{ display: 'flex', gap: 'var(--nx-space-3)' }}>
          <button className="nx-btn nx-btn--ghost-dark" onClick={() => setShowImport(true)}>Importar CSV</button>
          <button className="nx-btn" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancelar' : '+ Novo motorista'}</button>
        </div>
      </div>
      {showImport && (
        <CsvImportModal
          title="Importar motoristas"
          columns={['name', 'email', 'idTag', 'phone', 'document', 'status']}
          postUrl="/api/drivers/import-csv"
          templateUrl="/api/drivers/import-csv/template"
          templateFilename="drivers-template.csv"
          onClose={() => setShowImport(false)}
          onDone={reload}
        />
      )}
      {showForm && (
        <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
          <form onSubmit={create}>
            <div className="nx-filters">
              <label className="nx-field"><span>Nome</span><input name="name" required /></label>
              <label className="nx-field"><span>Documento</span><input name="document" /></label>
              <label className="nx-field"><span>E-mail</span><input name="email" type="email" /></label>
              <label className="nx-field"><span>Telefone</span><input name="phone" /></label>
              <label className="nx-field"><span>idTag OCPP</span><input name="idTag" pattern="[A-Za-z0-9._:-]{1,20}" /></label>
            </div>
            <button type="submit" className="nx-btn" style={{ marginTop: 'var(--nx-space-3)' }}>Criar</button>
          </form>
        </div>
      )}
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Nome</th><th>Documento</th><th>E-mail</th><th>Telefone</th><th>idTag</th><th>Status</th></tr></thead>
          <tbody>
            {(data?.drivers ?? []).map((d) => (
              <tr key={d.id}>
                <td>{d.name}</td>
                <td className="mono">{d.document ?? '—'}</td>
                <td className="mono">{d.email ?? '—'}</td>
                <td className="mono">{d.phone ?? '—'}</td>
                <td className="mono">{d.idTag ?? '—'}</td>
                <td>{badge(d.active ? 'ativo' : 'inativo', d.active ? 'ok' : 'err')}</td>
              </tr>
            ))}
            {!data?.drivers.length && <tr><td colSpan={6} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem motoristas.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

// ---------- 12. Visão Geral Financeira ----------
export function FinancialPage() {
  const { data } = useApi<{ month: string; transactions: number; totalKwh: number; peakKwh: number; offPeakKwh: number; costBrl: number; tariff: { peakBrl: number; offPeakBrl: number; peakStart: string; peakEnd: string }}>('/api/financial/overview');
  if (!data) return <div className="nx-loader">Carregando…</div>;
  return (
    <div>
      <div className="nx-dash__head">
        <h1>Financeiro — {data.month}</h1>
        <div style={{ display: 'flex', gap: 'var(--nx-space-2)', flexWrap: 'wrap' }}>
          {/* Ambos os endpoints exigem month=YYYY-MM. `data.month` já vem
              nesse formato — servidor calcula no timezone da instalação, então
              usamos EXATAMENTE o mesmo mês exibido, sem recalcular no cliente
              (evita divergência de fuso quando meia-noite cai no meio do dia
              UTC). */}
          <DownloadButton
            href={`/api/reports/xlsx/financial?month=${encodeURIComponent(data.month)}`}
            filename={`nxvolt-financial-${data.month}.xlsx`}
            label="Exportar XLSX"
            variant="primary"
            title={`Baixa a planilha financeira de ${data.month}`}
          />
          <DownloadButton
            href={`/api/reports/pdf/financial?month=${encodeURIComponent(data.month)}`}
            filename={`nxvolt-financial-${data.month}.pdf`}
            label="Exportar PDF"
            variant="secondary"
            title={`Baixa o resumo financeiro em PDF de ${data.month}`}
          />
        </div>
      </div>
      <p style={{ color: 'var(--nx-mute)' }}>Frotista: custo total baseado nas tarifas configuráveis em Horário de Ponta.</p>
      <div className="nx-kpi-grid">
        <div className="nx-kpi nx-kpi--navy"><div className="nx-kpi__label">Transações</div><div className="nx-kpi__value mono">{data.transactions}</div></div>
        <div className="nx-kpi nx-kpi--cyan"><div className="nx-kpi__label">Energia total</div><div className="nx-kpi__value mono">{data.totalKwh.toFixed(2)} kWh</div></div>
        <div className="nx-kpi nx-kpi--orange"><div className="nx-kpi__label">Ponta</div><div className="nx-kpi__value mono">{data.peakKwh.toFixed(2)} kWh</div><div className="nx-kpi__hint">R$ {data.tariff.peakBrl.toFixed(4)}/kWh · {data.tariff.peakStart}-{data.tariff.peakEnd}</div></div>
        <div className="nx-kpi nx-kpi--success"><div className="nx-kpi__label">Fora de ponta</div><div className="nx-kpi__value mono">{data.offPeakKwh.toFixed(2)} kWh</div><div className="nx-kpi__hint">R$ {data.tariff.offPeakBrl.toFixed(4)}/kWh</div></div>
      </div>
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-6)' }}>
        <div className="nx-kpi__label">Custo estimado do mês</div>
        <div style={{ fontSize: '3rem', fontWeight: 700, color: 'var(--nx-heading)' }} className="mono">R$ {data.costBrl.toFixed(2)}</div>
        <p style={{ color: 'var(--nx-mute)', fontSize: '.875rem' }}>Configure as tarifas em Configurações → financial.*</p>
      </div>
    </div>
  );
}

// ---------- 13. Plano Contratado ----------
export function PlanoContratadoPage() {
  const { data } = useApi<{ contractedDemandKw: number; peakStart: string; peakEnd: string; distributor: string | null; validFrom: string | null; validTo: string | null; peaksPerDay: Array<{ date: string; peakKw: number }> }>('/api/financial/plan');
  const maxKw = useMemo(() => Math.max(1, ...(data?.peaksPerDay ?? []).map((p) => p.peakKw)), [data]);
  return (
    <div>
      <h1>Plano Contratado</h1>
      <div className="nx-kpi-grid">
        <div className="nx-kpi nx-kpi--navy"><div className="nx-kpi__label">Demanda contratada</div><div className="nx-kpi__value mono">{data?.contractedDemandKw ?? 0} kW</div></div>
        <div className="nx-kpi nx-kpi--orange"><div className="nx-kpi__label">Horário de ponta</div><div className="nx-kpi__value mono" style={{ fontSize: '1.4rem' }}>{data?.peakStart}—{data?.peakEnd}</div></div>
        <div className="nx-kpi nx-kpi--cyan"><div className="nx-kpi__label">Distribuidora</div><div className="nx-kpi__value" style={{ fontSize: '1.2rem' }}>{data?.distributor ?? 'Não informada'}</div><div className="nx-kpi__hint">Vigência: {data?.validFrom ?? '—'} até {data?.validTo ?? '—'}</div></div>
      </div>
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-6)' }}>
        <h2 style={{ fontSize: '1rem' }}>Pico diário (últimos 30 dias)</h2>
        {!data?.peaksPerDay.length ? <p style={{ color: 'var(--nx-mute)' }}>Sem leituras.</p> : (
          <div style={{ display: 'grid', gap: 4 }}>
            {data.peaksPerDay.map((p) => {
              const over = data.contractedDemandKw > 0 && p.peakKw > data.contractedDemandKw;
              return (
                <div key={p.date} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 100px', alignItems: 'center', gap: 12, fontSize: '.875rem' }}>
                  <div className="mono">{p.date}</div>
                  <div style={{ background: 'var(--nx-bg-alt)', borderRadius: 4, height: 20, position: 'relative' }}>
                    <div style={{ width: `${(p.peakKw / maxKw) * 100}%`, background: over ? 'var(--nx-danger)' : 'var(--nx-cyan)', height: '100%', borderRadius: 4 }} />
                  </div>
                  <div className="mono" style={{ color: over ? 'var(--nx-danger)' : 'var(--nx-ink)' }}>{p.peakKw.toFixed(1)} kW</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- 14. Rel. de Consumos ----------
export function RelConsumoPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const q = useMemo(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', new Date(from).toISOString());
    if (to) p.set('to', new Date(to).toISOString());
    return p.toString();
  }, [from, to]);
  const { data } = useApi<{ rows: Array<{ driver: string; idTag: string; transactions: number; kwh: number; costBrl: number }>; totalTransactions: number }>(`/api/financial/rel-consumo?${q}`, [q]);
  function csv() {
    const rows = data?.rows ?? [];
    const lines = ['motorista;idTag;transacoes;kwh;custo_brl'];
    for (const r of rows) lines.push(`${r.driver};${r.idTag};${r.transactions};${r.kwh};${r.costBrl}`);
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `rel-consumo-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }
  // XLSX oficial: reaproveita o export de `sessions` cobrindo o mesmo período
  // — é a fonte primária das linhas agregadas nesta tela. O endpoint exige
  // `from` e `to`; enquanto o operador não escolhe, o botão fica desabilitado
  // com título explicativo.
  const xlsxHref = useMemo(() => {
    if (!from || !to) return null;
    const p = new URLSearchParams();
    p.set('from', new Date(from).toISOString());
    p.set('to', new Date(to).toISOString());
    return `/api/reports/xlsx/sessions?${p.toString()}`;
  }, [from, to]);
  const xlsxFilename = useMemo(() => {
    const f = from ? new Date(from).toISOString().slice(0, 10) : 'inicio';
    const t = to ? new Date(to).toISOString().slice(0, 10) : 'fim';
    return `nxvolt-rel-consumo-${f}_${t}.xlsx`;
  }, [from, to]);
  return (
    <div>
      <div className="nx-dash__head">
        <h1>Rel. de Consumos</h1>
        <div style={{ display: 'flex', gap: 'var(--nx-space-2)', flexWrap: 'wrap' }}>
          <button className="nx-btn nx-btn--ghost-dark" onClick={csv} disabled={!data?.rows.length}>⤓ CSV</button>
          <DownloadButton
            href={xlsxHref ?? '#'}
            filename={xlsxFilename}
            label="Exportar XLSX"
            variant="primary"
            disabled={!xlsxHref}
            title={
              xlsxHref
                ? 'Baixa as sessões do período em XLSX (mesma base do agregado desta tela)'
                : 'Selecione um período para exportar'
            }
          />
        </div>
      </div>
      <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
        <div className="nx-filters">
          <label className="nx-field"><span>De</span><input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="nx-field"><span>Até</span><input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        </div>
      </div>
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Motorista</th><th>idTag</th><th>Transações</th><th>Energia (kWh)</th><th>Custo (R$)</th></tr></thead>
          <tbody>
            {(data?.rows ?? []).map((r) => (
              <tr key={r.idTag}>
                <td>{r.driver}</td>
                <td className="mono">{r.idTag}</td>
                <td className="mono">{r.transactions}</td>
                <td className="mono">{r.kwh.toFixed(3)}</td>
                <td className="mono">R$ {r.costBrl.toFixed(2)}</td>
              </tr>
            ))}
            {!data?.rows.length && <tr><td colSpan={5} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem transações.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

// ---------- 15. Horário de Ponta ----------
export function HorarioPontaPage() {
  const { data } = useApi<{ peakStart: string; peakEnd: string; peakBrl: number; offPeakBrl: number; hourlyKwh: Array<{ hour: number; kwh: number }> }>('/api/financial/peak');
  const maxKwh = useMemo(() => Math.max(1, ...(data?.hourlyKwh ?? []).map((h) => h.kwh)), [data]);
  const inPeak = (h: number) => {
    if (!data) return false;
    const [ps] = data.peakStart.split(':').map(Number);
    const [pe] = data.peakEnd.split(':').map(Number);
    return ps! <= pe! ? h >= ps! && h < pe! : h >= ps! || h < pe!;
  };
  return (
    <div>
      <h1>Horário de Ponta</h1>
      <div className="nx-kpi-grid">
        <div className="nx-kpi nx-kpi--orange"><div className="nx-kpi__label">Janela de ponta</div><div className="nx-kpi__value mono" style={{ fontSize: '1.6rem' }}>{data?.peakStart}—{data?.peakEnd}</div></div>
        <div className="nx-kpi nx-kpi--navy"><div className="nx-kpi__label">Tarifa ponta</div><div className="nx-kpi__value mono">R$ {(data?.peakBrl ?? 0).toFixed(4)}/kWh</div></div>
        <div className="nx-kpi nx-kpi--success"><div className="nx-kpi__label">Tarifa fora de ponta</div><div className="nx-kpi__value mono">R$ {(data?.offPeakBrl ?? 0).toFixed(4)}/kWh</div></div>
      </div>
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-6)' }}>
        <h2 style={{ fontSize: '1rem' }}>Distribuição de energia por hora do dia</h2>
        <div style={{ display: 'grid', gap: 4 }}>
          {(data?.hourlyKwh ?? []).map((h) => (
            <div key={h.hour} style={{ display: 'grid', gridTemplateColumns: '50px 1fr 100px', alignItems: 'center', gap: 12, fontSize: '.875rem' }}>
              <div className="mono">{String(h.hour).padStart(2, '0')}h</div>
              <div style={{ background: 'var(--nx-bg-alt)', borderRadius: 4, height: 16, position: 'relative' }}>
                <div style={{ width: `${(h.kwh / maxKwh) * 100}%`, background: inPeak(h.hour) ? 'var(--nx-orange)' : 'var(--nx-cyan)', height: '100%', borderRadius: 4 }} />
              </div>
              <div className="mono">{h.kwh.toFixed(2)} kWh</div>
            </div>
          ))}
        </div>
        <p style={{ color: 'var(--nx-mute)', fontSize: '.75rem', marginTop: 'var(--nx-space-3)' }}>Laranja = janela de ponta, ciano = fora de ponta.</p>
      </div>
    </div>
  );
}

// ---------- Sistema — Logs ----------
export function SystemLogsPage() {
  const { data } = useApi<{ logs: Array<{ id: number; userId: number | null; userEmail: string | null; action: string; targetKind: string | null; targetId: string | null; meta: string | null; ip: string | null; createdAt: string }> }>('/api/system-logs');
  const { user } = useAuth();
  if (user?.role !== 'admin') return <p>Sem permissão.</p>;
  return (
    <div>
      <h1>Logs do Sistema</h1>
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Quando</th><th>Usuário</th><th>Ação</th><th>Alvo</th><th>Meta</th><th>IP</th></tr></thead>
          <tbody>
            {(data?.logs ?? []).map((l) => (
              <tr key={l.id}>
                <td className="mono" style={{ fontSize: '.75rem' }}>{fmtDate(l.createdAt)}</td>
                <td className="mono">{l.userEmail ?? l.userId ?? '—'}</td>
                <td className="mono">{l.action}</td>
                <td className="mono">{l.targetKind}{l.targetId ? `#${l.targetId}` : ''}</td>
                <td className="mono" style={{ fontSize: '.7rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.meta}</td>
                <td className="mono">{l.ip ?? '—'}</td>
              </tr>
            ))}
            {!data?.logs.length && <tr><td colSpan={6} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem eventos registrados.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}
